/**
 * Search Management - Moon Moon Agency
 * Handles search overlay functionality
 */

/*********************************************************************************************
 * 🔍 SEARCH MODULE
 */
const SearchManager = {
	// Local configuration
	config: {
		selectors: {
			searchContainer: '[data-search-container]',
			searchInput: '[data-search-input]',
			searchToggle: '[data-search-toggle]',
			searchClose: '[data-search-close]',
			searchClear: '[data-search-clear]',
			predictiveSearch: '[data-predictive-search]'
		},
		classes: {
			stopScrolling: 'mm-stop-scrolling',
			open: 'mm-open'
		},
		timing: {
			searchFocusDelay: 100,
			searchBlurDelay: 100
		}
	},

	// Open state (replaces html class check)
	_isOpen: false,
	_savedScrollY: undefined,

	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Initialize the search module
	 */
	init() {
		const searches = window.getElements(this.config.selectors.searchContainer);
		if (!searches.length) return;

		// Search containers are closed by default: inert removes them from tab order
		searches.forEach((s) => {
			s.inert = true;
			s.setAttribute('aria-hidden', 'true');
		});

		this._bindEvents();
		window.log('info', 'SearchManager', 'Initialized');
	},

	/**
	 * Bind all static event listeners (called once at init)
	 */
	_bindEvents() {
		// Close button (background overlay or dedicated close btn)
		const searchCloseBtn = window.getElement(this.config.selectors.searchClose);
		if (searchCloseBtn) {
			searchCloseBtn.addEventListener('click', () => this.hide());
		}

		// Clear button (inside search input)
		const searchClearBtn = window.getElement(this.config.selectors.searchClear);
		if (searchClearBtn) {
			searchClearBtn.addEventListener('click', () => this.hide());
		}

		// Predictive search touchstart — enregistré une seule fois (pas dans show())
		const predictiveSearch = window.getElement(this.config.selectors.predictiveSearch);
		if (predictiveSearch) {
			predictiveSearch.addEventListener('touchstart', () => {
				const searchInput = window.getElement(this.config.selectors.searchInput);
				if (searchInput) {
					setTimeout(() => searchInput.blur(), this.config.timing.searchBlurDelay);
				}
			}, { passive: true });
		}

		// Clic en dehors du panel predictive-search → ferme la search
		document.addEventListener('click', (e) => {
			if (!this._isOpen) return;
			if (e.target.closest('predictive-search')) return;
			if (e.target.closest(this.config.selectors.searchToggle)) return;
			this.hide();
		});

		// Suggestions dynamiques (collections + articles) peuplées par predictive-search.js
		document.addEventListener('mm:search-suggestions', (e) => {
			const { collections = [], articles = [], query = '' } = e.detail;
			const escapeHtml = (str) => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
			const highlight = (title) => {
				const safe = escapeHtml(title);
				if (!query) return safe;
				const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
				return safe.replace(new RegExp(`(${escaped})`, 'gi'), '<span class="mm-search-highlight">$1</span>');
			};
			window.getElements('[data-search-suggestion-list]').forEach((list) => {
				let html = '';
				collections.forEach((c) => {
					html += `<a href="${escapeHtml(c.url)}" class="mm-search-suggestion-item text-sm mm-black-text">${highlight(c.title)}</a>`;
				});
				articles.forEach((a) => {
					html += `<a href="${escapeHtml(a.url)}" class="mm-search-suggestion-item text-sm mm-black-text">${highlight(a.title)}</a>`;
				});
				list.innerHTML = html;
				const section = list.closest('[data-search-sidebar-suggestions]');
				if (section) {
					section.style.display = (collections.length || articles.length) ? 'flex' : 'none';
				}
			});
		});
	},

	/**
	 * Update aria-expanded on all search toggle buttons
	 */
	_updateToggleAria(isOpen) {
		window.getElements(this.config.selectors.searchToggle).forEach((btn) => {
			btn.setAttribute('aria-expanded', String(isOpen));
		});
	},

	/**
	 * Setup focus trap for search overlay
	 */
	setupFocusTrap(searchContainer, triggerElement) {
		const focusableElements = searchContainer.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes search
			if (e.key === 'Escape') {
				e.preventDefault();
				const trigger = this.focusTrap.triggerElement; // save before hide() nullifies it
				this.hide();
				trigger?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		window.log('info', 'SearchManager', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'SearchManager', 'Focus trap removed');
		}
	},

	/**
	 * Show search overlay
	 */
	show(triggerElement) {
		const searches = window.getElements(this.config.selectors.searchContainer);
		const allInputs = window.getElements(this.config.selectors.searchInput);

		if (!searches.length || !allInputs.length) {
			window.log('warn', 'SearchManager', 'Required elements not found');
			return;
		}

		this._savedScrollY = window.scrollY;
		this._isOpen = true;
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this._savedScrollY}px`;
		document.body.style.width = '100%';
		document.body.style.overflow = 'hidden';
		if (window.LenisManager?.instance) {
			window.LenisManager.shouldBeStopped = true;
			window.LenisManager.instance.stop();
		}
		searches.forEach((s) => {
			s.inert = false;
			s.style.removeProperty('visibility');
			s.classList.add(this.config.classes.open);
			s.setAttribute('aria-hidden', 'false');
		});
		this._updateToggleAria(true);

		// Focus synchronously to trigger mobile keyboard (must stay in user gesture chain)
		const visibleInput = Array.from(allInputs).find((el) => el.offsetParent !== null) || allInputs[0];
		visibleInput.focus();

		requestAnimationFrame(() => {
			const filter = window.getElement('[data-filter]');
			if (filter) filter.classList.add('active');
			if (window.NavigationManager?.showBackgroundFilter) {
				window.NavigationManager.showBackgroundFilter();
			}
		});

		setTimeout(() => {
			this.setupFocusTrap(searches[0], triggerElement);
		}, this.config.timing.searchFocusDelay);

		window.emit('global:search-opened');
	},

	/**
	 * Hide search overlay
	 */
	hide(keepFilterActive = false, keepStopScrolling = false) {
		const searches = window.getElements(this.config.selectors.searchContainer);
		const allInputs = window.getElements(this.config.selectors.searchInput);

		if (!searches.length || !allInputs.length) {
			window.log('warn', 'SearchManager', 'Required elements not found for hiding');
			return;
		}

		if (!keepStopScrolling) {
			this._isOpen = false;
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			document.body.style.overflow = '';
			window.LenisManager?.resume();
			if (this._savedScrollY !== undefined) {
				window.scrollTo({ top: this._savedScrollY, behavior: 'instant' });
				this._savedScrollY = undefined;
			}
		}
		searches.forEach((s) => {
			s.classList.remove(this.config.classes.open);
			s.setAttribute('aria-hidden', 'true');
			s.inert = true;
		});
		this._updateToggleAria(false);

		this.removeFocusTrap();

		if (!keepFilterActive) {
			const filter = window.getElement('[data-filter]');
			if (filter) filter.classList.remove('active');
			if (window.NavigationManager?.hideBackgroundFilter) {
				window.NavigationManager.hideBackgroundFilter();
			}
		}

		allInputs.forEach((input) => {
			input.value = '';
			input.dispatchEvent(new Event('input'));
		});

		window.emit('global:search-closed', { keepFilterActive, keepStopScrolling });
	}
};

/*********************************************************************************************
 * 🌍 GLOBAL ACCESS
 */
window.SearchManager = SearchManager;

/*********************************************************************************************
 * 🔄 BACKWARD COMPATIBILITY
 */
window.mmShowSearch = (triggerElement) => SearchManager.show(triggerElement);
window.mmHideSearch = (keepFilter, keepScrolling) => SearchManager.hide(keepFilter, keepScrolling);

/*********************************************************************************************
 * 🎯 AUTO-INITIALIZATION
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => SearchManager.init());
} else {
	SearchManager.init();
}
